// Preferences Quiz View - Onboarding flow to capture taste profile
import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Check, Sparkles } from 'lucide-react';
import { CigarIcon } from '../components/Icons';
import { FlavorNote, Strength, UserPreferences } from '../types';

interface PreferencesQuizViewProps {
  onComplete: (preferences: UserPreferences) => void;
  onSkip: () => void;
  existingPreferences?: UserPreferences;
}

type Step = 'experience' | 'strength' | 'flavors' | 'complete';

const FLAVOR_OPTIONS: { value: FlavorNote; label: string; description: string }[] = [
  { value: 'earthy', label: 'Earthy', description: 'Soil, mushroom, moss' },
  { value: 'spicy', label: 'Spicy', description: 'Pepper, cinnamon, clove' },
  { value: 'sweet', label: 'Sweet', description: 'Honey, caramel, dried fruit' },
  { value: 'woody', label: 'Woody', description: 'Oak, cedar, sandalwood' },
  { value: 'creamy', label: 'Creamy', description: 'Butter, cream, milk chocolate' },
  { value: 'nutty', label: 'Nutty', description: 'Almond, walnut, hazelnut' },
  { value: 'coffee', label: 'Coffee', description: 'Espresso, roasted beans' },
  { value: 'cocoa', label: 'Cocoa', description: 'Dark chocolate, cacao' },
  { value: 'leather', label: 'Leather', description: 'Rich, aged leather' },
  { value: 'floral', label: 'Floral', description: 'Rose, lavender, jasmine' },
  { value: 'citrus', label: 'Citrus', description: 'Orange, lemon zest' },
  { value: 'herbal', label: 'Herbal', description: 'Tea, grass, herbs' },
];

export const PreferencesQuizView: React.FC<PreferencesQuizViewProps> = ({
  onComplete,
  onSkip,
  existingPreferences,
}) => {
  const [step, setStep] = useState<Step>('experience');
  const [experience, setExperience] = useState<UserPreferences['experience']>(
    existingPreferences?.experience || 'beginner'
  );
  const [strength, setStrength] = useState<Strength>(
    existingPreferences?.strength || 'medium'
  );
  const [selectedFlavors, setSelectedFlavors] = useState<FlavorNote[]>(
    existingPreferences?.flavors || []
  );

  const steps: Step[] = ['experience', 'strength', 'flavors', 'complete'];
  const currentStepIndex = steps.indexOf(step);

  const handleNext = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setStep(steps[nextIndex]);
    }
  };

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setStep(steps[prevIndex]);
    }
  };

  const handleComplete = () => {
    onComplete({
      experience,
      strength,
      flavors: selectedFlavors,
    });
  };

  const toggleFlavor = (flavor: FlavorNote) => {
    if (selectedFlavors.includes(flavor)) {
      setSelectedFlavors(selectedFlavors.filter(f => f !== flavor));
    } else if (selectedFlavors.length < 5) {
      setSelectedFlavors([...selectedFlavors, flavor]);
    }
  };

  return (
    <div className="min-h-screen bg-stone-900 flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2">
          <CigarIcon className="w-7 h-7 text-amber-500" />
          <span className="text-lg font-bold text-white">Cigar Advisor</span>
        </div>
        <button
          onClick={onSkip}
          className="text-white/50 hover:text-white text-sm"
        >
          Skip for now
        </button>
      </header>

      {/* Progress bar */}
      <div className="px-6 mb-8">
        <div className="flex gap-2">
          {steps.slice(0, -1).map((s, i) => (
            <div
              key={s}
              className={`flex-1 h-1 rounded-full transition-colors ${
                i <= currentStepIndex ? 'bg-amber-500' : 'bg-white/20'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        {step === 'experience' && (
          <div className="w-full max-w-lg">
            <h1 className="text-3xl font-bold text-white mb-2 text-center" style={{ fontFamily: 'Georgia, serif' }}>
              <span className="italic">What's your</span>{' '}
              <span className="text-amber-500 italic">experience level?</span>
            </h1>
            <p className="text-white/60 text-center mb-8">
              This helps us tailor recommendations to you
            </p>

            <div className="space-y-3">
              <ExperienceOption
                value="beginner"
                label="Beginner"
                description="New to cigars, exploring different options"
                selected={experience === 'beginner'}
                onClick={() => setExperience('beginner')}
              />
              <ExperienceOption
                value="intermediate"
                label="Intermediate"
                description="Know what I like, but open to new discoveries"
                selected={experience === 'intermediate'}
                onClick={() => setExperience('intermediate')}
              />
              <ExperienceOption
                value="aficionado"
                label="Aficionado"
                description="Deep knowledge, looking for exceptional cigars"
                selected={experience === 'aficionado'}
                onClick={() => setExperience('aficionado')}
              />
            </div>
          </div>
        )}

        {step === 'strength' && (
          <div className="w-full max-w-lg">
            <h1 className="text-3xl font-bold text-white mb-2 text-center" style={{ fontFamily: 'Georgia, serif' }}>
              <span className="italic">Preferred</span>{' '}
              <span className="text-amber-500 italic">strength?</span>
            </h1>
            <p className="text-white/60 text-center mb-8">
              How bold do you like your cigars?
            </p>

            <div className="space-y-3">
              <StrengthOption
                value="mild"
                label="Mild"
                description="Smooth, gentle, easy-going"
                selected={strength === 'mild'}
                onClick={() => setStrength('mild')}
                color="bg-green-500/20 border-green-500/50"
              />
              <StrengthOption
                value="medium"
                label="Medium"
                description="Balanced flavor with some complexity"
                selected={strength === 'medium'}
                onClick={() => setStrength('medium')}
                color="bg-amber-500/20 border-amber-500/50"
              />
              <StrengthOption
                value="medium-full"
                label="Medium-Full"
                description="Rich flavors with noticeable kick"
                selected={strength === 'medium-full'}
                onClick={() => setStrength('medium-full')}
                color="bg-orange-500/20 border-orange-500/50"
              />
              <StrengthOption
                value="full"
                label="Full"
                description="Bold, intense, powerful"
                selected={strength === 'full'}
                onClick={() => setStrength('full')}
                color="bg-red-500/20 border-red-500/50"
              />
            </div>
          </div>
        )}

        {step === 'flavors' && (
          <div className="w-full max-w-2xl">
            <h1 className="text-3xl font-bold text-white mb-2 text-center" style={{ fontFamily: 'Georgia, serif' }}>
              <span className="italic">Flavor</span>{' '}
              <span className="text-amber-500 italic">preferences</span>
            </h1>
            <p className="text-white/60 text-center mb-2">
              Select up to 5 flavors you enjoy
            </p>
            <p className="text-amber-500/80 text-center text-sm mb-8">
              {selectedFlavors.length}/5 selected
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {FLAVOR_OPTIONS.map(flavor => (
                <button
                  key={flavor.value}
                  onClick={() => toggleFlavor(flavor.value)}
                  disabled={selectedFlavors.length >= 5 && !selectedFlavors.includes(flavor.value)}
                  className={`p-4 rounded-xl border text-left transition-all ${
                    selectedFlavors.includes(flavor.value)
                      ? 'bg-amber-500/20 border-amber-500/50'
                      : 'bg-white/5 border-white/10 hover:bg-white/10 disabled:opacity-40'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white font-medium">{flavor.label}</span>
                    {selectedFlavors.includes(flavor.value) && (
                      <Check className="w-4 h-4 text-amber-500" />
                    )}
                  </div>
                  <p className="text-white/50 text-xs">{flavor.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 'complete' && (
          <div className="w-full max-w-lg text-center">
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-amber-500" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              <span className="italic">You're all</span>{' '}
              <span className="text-amber-500 italic">set!</span>
            </h1>
            <p className="text-white/60 mb-8">
              Your taste profile is ready. We'll use this to generate<br />
              personalized questions and recommendations.
            </p>

            <div className="bg-stone-800 rounded-xl p-4 mb-8 text-left">
              <h3 className="text-white/50 text-xs uppercase tracking-wider mb-3">Your Profile</h3>
              <div className="space-y-2">
                <ProfileItem label="Experience" value={experience.charAt(0).toUpperCase() + experience.slice(1)} />
                <ProfileItem label="Strength" value={strength.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} />
                <ProfileItem label="Flavors" value={selectedFlavors.map(f => f.charAt(0).toUpperCase() + f.slice(1)).join(', ') || 'None selected'} />
              </div>
            </div>

            <button
              onClick={handleComplete}
              className="w-full py-4 rounded-xl bg-amber-500 text-stone-900 font-semibold text-lg hover:bg-amber-400 transition-colors"
            >
              Start Exploring
            </button>
          </div>
        )}
      </main>

      {/* Navigation */}
      {step !== 'complete' && (
        <footer className="p-6">
          <div className="flex justify-between max-w-lg mx-auto">
            <button
              onClick={handleBack}
              disabled={currentStepIndex === 0}
              className="flex items-center gap-2 px-6 py-3 rounded-xl text-white/60 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>
            <button
              onClick={handleNext}
              disabled={step === 'flavors' && selectedFlavors.length === 0}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-amber-500 text-stone-900 font-semibold hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </footer>
      )}
    </div>
  );
};

const ExperienceOption: React.FC<{
  value: string;
  label: string;
  description: string;
  selected: boolean;
  onClick: () => void;
}> = ({ label, description, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full p-4 rounded-xl border text-left transition-all ${
      selected
        ? 'bg-amber-500/20 border-amber-500/50'
        : 'bg-white/5 border-white/10 hover:bg-white/10'
    }`}
  >
    <div className="flex items-center justify-between">
      <div>
        <h3 className="text-white font-medium mb-1">{label}</h3>
        <p className="text-white/50 text-sm">{description}</p>
      </div>
      {selected && (
        <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
          <Check className="w-4 h-4 text-stone-900" />
        </div>
      )}
    </div>
  </button>
);

const StrengthOption: React.FC<{
  value: string;
  label: string;
  description: string;
  selected: boolean;
  onClick: () => void;
  color: string;
}> = ({ label, description, selected, onClick, color }) => (
  <button
    onClick={onClick}
    className={`w-full p-4 rounded-xl border text-left transition-all ${
      selected ? color : 'bg-white/5 border-white/10 hover:bg-white/10'
    }`}
  >
    <div className="flex items-center justify-between">
      <div>
        <h3 className="text-white font-medium mb-1">{label}</h3>
        <p className="text-white/50 text-sm">{description}</p>
      </div>
      {selected && (
        <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
          <Check className="w-4 h-4 text-stone-900" />
        </div>
      )}
    </div>
  </button>
);

const ProfileItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-white/50">{label}</span>
    <span className="text-white font-medium">{value}</span>
  </div>
);

export default PreferencesQuizView;
